// DropPin page wake — v0.256.0
// ---------------------------------------------------------------------
// Brett: "sync on droppin stopped again. i think it was when the ipad went to
// sleep. We cant have that."
//
// What an iPad does to a home-screen app when the screen locks: its JavaScript
// is frozen, timers stop, a request in flight either fails ("Load failed") or
// hangs, and on waking the Wi-Fi takes a few seconds to come back. It does NOT
// reliably fire 'offline' or 'online' around any of that, and there is no
// background sync. So the sync's three quick retries all landed in the seconds
// after waking, all failed, and the sync stopped, with nothing to start it again.
//
// This module is the page's own record of naps, for the outbox to read:
//   onPageWake(fn)          → unsubscribe()   fires when the page comes back
//   pageHiddenNow()         → boolean
//   napGeneration()         → number, +1 each time the page is hidden
//   sleptRecently(gen, ms)  → the page napped since `gen`, is hidden now, or
//                             woke less than `ms` ago
//   waitUntilAwake()        → resolves once the page is visible
//   waitForOnline(maxMs)    → resolves when the device says it is online, or
//                             after maxMs (the device's word is not trusted)
//   holdScreenAwake()       → keep the screen on (Screen Wake Lock) until
//   releaseScreen()           released; taken again each time the page returns
//
// Safe to import under node (the tests do): with no document nothing is ever
// hidden and every wait resolves at once.

const hasDoc = typeof document !== 'undefined' && typeof document.addEventListener === 'function';

let _generation = 0;
let _hiddenSince = 0;
let _lastWokeAt = 0;
const _wakeListeners = new Set();

function isHidden() {
  return hasDoc && document.visibilityState === 'hidden';
}

function wake() {
  if (!_hiddenSince) return;
  _hiddenSince = 0;
  _lastWokeAt = Date.now();
  _wakeListeners.forEach((fn) => { try { fn(); } catch (_) {} });
}

if (hasDoc) {
  if (isHidden()) { _hiddenSince = Date.now(); _generation++; }
  document.addEventListener('visibilitychange', () => {
    if (isHidden()) {
      if (!_hiddenSince) { _hiddenSince = Date.now(); _generation++; }
    } else {
      wake();
    }
  });
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    // Restored from the back/forward cache: the page slept whatever it said.
    window.addEventListener('pageshow', (e) => {
      if (e && e.persisted) {
        if (!_hiddenSince) { _hiddenSince = Date.now(); _generation++; }
        wake();
      }
    });
    window.addEventListener('pagehide', () => {
      if (!_hiddenSince) { _hiddenSince = Date.now(); _generation++; }
    });
  }
}

export function onPageWake(fn) {
  _wakeListeners.add(fn);
  return () => _wakeListeners.delete(fn);
}

export function pageHiddenNow() {
  return isHidden();
}

export function napGeneration() {
  return _generation;
}

export function sleptRecently(gen, ms = 15000) {
  if (_generation !== gen) return true;
  if (isHidden()) return true;
  return !!_lastWokeAt && Date.now() - _lastWokeAt < ms;
}

export function lastWokeAt() {
  return _lastWokeAt;
}

export function waitUntilAwake() {
  if (!isHidden()) return Promise.resolve();
  return new Promise((resolve) => {
    const off = onPageWake(() => { off(); resolve(); });
  });
}

export function waitForOnline(maxMs = 20000) {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (!nav || nav.onLine !== false || typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    let timer = null;
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    timer = setTimeout(done, maxMs);
    window.addEventListener('online', done);
  });
}

// ── Screen wake lock ──────────────────────────────────────────────
//
// While a sync runs the screen stays on, so the iPad does not lock part-way
// through. iOS supports this in a home-screen app from 18.4; elsewhere the
// request fails and the sync carries on exactly as before. The lock is lost
// whenever the page is hidden, so it is taken again on return while still held.
let _holds = 0;
let _sentinel = null;

async function acquire() {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (!_holds || _sentinel || !nav || !nav.wakeLock || isHidden()) return;
  try {
    const s = await nav.wakeLock.request('screen');
    if (!_holds) { try { await s.release(); } catch (_) {} return; }
    _sentinel = s;
    s.addEventListener?.('release', () => { if (_sentinel === s) _sentinel = null; });
  } catch (_) {
    // Not supported, or refused (low battery mode): the sync does not need it.
  }
}

onPageWake(() => { acquire(); });

export function holdScreenAwake() {
  _holds++;
  acquire();
}

export function releaseScreen() {
  _holds = Math.max(0, _holds - 1);
  if (_holds || !_sentinel) return;
  const s = _sentinel;
  _sentinel = null;
  Promise.resolve().then(() => s.release()).catch(() => {});
}
